import { HttpErrorResponse } from '@angular/common/http';
import { computed, Signal } from '@angular/core';
import { OrderDto, TradingControllerService } from '@api/client';
import { tapResponse } from '@ngrx/operators';
import {
  patchState,
  signalStoreFeature,
  type,
  withComputed,
  withMethods,
  withState,
} from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { NotificationService } from '@shared/services/notifications/notifications.service';
import { mergeMap, pipe } from 'rxjs';
import { OrderViewModel } from '../dashboard.interfaces';
import { mapOrders } from './orders.utils';

export const useActiveOrders = () =>
  signalStoreFeature(
    {
      props: {
        _activeAccountId: type<Signal<number | undefined>>(),
        _notificationService: type<NotificationService>(),
        _tradingService: type<TradingControllerService>(),
      },
    },
    withState({
      activeOrders: new Array<OrderDto>(),
    }),
    withComputed(store => {
      const activeOrderViewModels = computed<OrderViewModel[]>(() =>
        store.activeOrders().map(mapOrders),
      );

      return { activeOrderViewModels };
    }),
    withMethods(store => {
      const setActiveOrders = (activeOrders: OrderDto[]) =>
        patchState(store, { activeOrders });

      const cancelAllActiveOrders = rxMethod<void>(
        pipe(
          mergeMap(() =>
            store._tradingService
              .cancelOrder({ accountId: store._activeAccountId() })
              .pipe(
                tapResponse({
                  next: () => undefined,
                  error: (error: HttpErrorResponse) =>
                    store._notificationService.showErrorNotification(
                      `The active orders couldn't be cancelled`,
                      error,
                    ),
                }),
              ),
          ),
        ),
      );

      const cancelActiveOrder = rxMethod<number>(
        pipe(
          mergeMap(orderId =>
            store._tradingService
              .cancelOrder({ orderId, accountId: store._activeAccountId() })
              .pipe(
                tapResponse({
                  next: () => undefined,
                  error: (error: HttpErrorResponse) =>
                    store._notificationService.showErrorNotification(
                      `The active order couldn't be cancelled`,
                      error,
                    ),
                }),
              ),
          ),
        ),
      );

      return { setActiveOrders, cancelAllActiveOrders, cancelActiveOrder };
    }),
  );
